/**
 * APP.JSX v2.8
 * ✅ Todo de v2.7 preservado
 * ✅ TutorialProvider y TutorialOverlay unificados
 *    — TutorialProvider vive dentro de AppProvider
 *    — TutorialOverlay se monta una sola vez en App
 */
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { AuthProvider } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { FirebaseProvider } from './context/FirebaseContext';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import SyncStatus from './components/SyncStatus';
import AdminRoute from './components/AdminRoute';
import TutorialOverlay from './components/TutorialOverlay';
import analyticsService from './services/analyticsService';
import UpdateExamplesButton from './components/UpdateExamplesButton';
import { CustomToaster } from './components/toastConfig';
import { DoubleSpinner } from './components/SkeletonLoaders';

// ============================================
// 🚀 LAZY LOADING
// ============================================
const StockPage            = lazy(() => import('./pages/StockPage'));
const RecipesPage          = lazy(() => import('./pages/RecipesPage'));
const ComponentsPage       = lazy(() => import('./pages/ComponentsPage'));
const PlannerPage          = lazy(() => import('./pages/PlannerPage'));
const ShoppingPage         = lazy(() => import('./pages/ShoppingPage'));
const CookablePage         = lazy(() => import('./pages/CookablePage'));
const FamilyPage           = lazy(() => import('./pages/FamilyPage'));
const StatsPage            = lazy(() => import('./pages/StatsPage'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const AdminPanel           = lazy(() => import('./pages/AdminPanel'));

// ============================================
// 💫 Loading Component
// ============================================
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900">
      <DoubleSpinner />
    </div>
  );
}

// ============================================
// 📊 Analytics Wrapper — solo page views
// retention ping y setUserProperties → AuthContext
// ============================================
function AnalyticsWrapper({ children }) {
  const location = useLocation();

  useEffect(() => {
    analyticsService.trackPageView(getPageName(location.pathname), location.pathname);
  }, [location.pathname]);

  return children;
}

function getPageName(path) {
  const routes = {
    '/':              'Stock',
    '/components':    'Componentes',
    '/recipes':       'Recetas',
    '/planner':       'Planificador',
    '/shopping':      'Compras',
    '/cookable':      'Qué Cocinar',
    '/family':        'Familia',
    '/stats':         'Estadísticas',
    '/notifications': 'Notificaciones',
    '/admin':         'Admin Panel',
  };
  return routes[path] || 'Unknown';
}

// ============================================
// 🏠 App
// ============================================
function App() {
  useEffect(() => {
    analyticsService.init();
  }, []);

  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <FirebaseProvider>
            <AppProvider>
              <AnalyticsWrapper>
                <Routes>
                  <Route
                    path="/*"
                    element={
                      <Layout>
                        <SyncStatus />
                        <Suspense fallback={<PageLoader />}>
                          <Routes>
                            {/* Páginas Principales */}
                            <Route path="/"           element={<StockPage />} />
                            <Route path="/components" element={<ComponentsPage />} />
                            <Route path="/recipes"    element={<RecipesPage />} />
                            <Route path="/planner"    element={<PlannerPage />} />
                            <Route path="/shopping"   element={<ShoppingPage />} />
                            <Route path="/cookable"   element={<CookablePage />} />
                            {/* Features Avanzadas */}
                            <Route path="/family"        element={<FamilyPage />} />
                            <Route path="/stats"         element={<StatsPage />} />
                            <Route path="/notifications" element={<NotificationSettings />} />
                            {/* Admin protegido */}
                            <Route
                              path="/admin"
                              element={
                                <AdminRoute>
                                  <AdminPanel />
                                </AdminRoute>
                              }
                            />
                          </Routes>
                        </Suspense>
                      </Layout>
                    }
                  />
                </Routes>
              </AnalyticsWrapper>
              {/* Tutorial — una sola instancia global */}
              <TutorialOverlay />
              {/* Global Components */}
              <CustomToaster />
              <UpdateExamplesButton />
            </AppProvider>
          </FirebaseProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;